import { observer } from 'mobx-react-lite';

import _ from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';
import { useEffect } from 'react';
import { StockIndexResponse, StockSectorResponse } from '../../../response/responseSchema';
import { apiClient } from '@app/apiClient';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import { useCommonNavigate } from '@client/component/CommonLink';
import ChangeBarGraph, { ChangeBarGraphItem } from './ChangeBarGraph';
import TargetReturnButton from './TargetReturnButton';

class ViewStore {
    sectorPerformance: StockSectorResponse[] = [];
    indexPerformance: StockIndexResponse[] = [];
    briefDate: string | null = null;
    targetReturn: 'daily' | 'mtd' | 'ytd' = 'daily';

    graphItems: ChangeBarGraphItem<{ sectorSlug: string }>[] = [];

    constructor() {
        makeAutoObservable(this);
    }

    async fetch() {
        const [sectorPerformance, indexPerformance] = await Promise.all([
            apiClient.stockSector.public.getSectorPerformance.$query(),
            apiClient.stockIndex.public.getIndexPerformance.$query(),
        ]);

        runInAction(() => {
            this.sectorPerformance = sectorPerformance;
            this.indexPerformance = indexPerformance;
            this.briefDate = _.last(indexPerformance)?.price?.date || null;

            this.makeGraphItems();
        });
    }

    private makeGraphItems() {
        // S&P 500 변화율 추출
        const sp500Index = this.indexPerformance.find((index) => index.ticker === '_SP500');

        let sp500Return = 0;
        if (this.targetReturn === 'daily') {
            sp500Return = Number(sp500Index?.price?.dailyReturn) || 0;
        } else if (this.targetReturn === 'mtd') {
            sp500Return = Number(sp500Index?.price?.mtdReturn) || 0;
        } else if (this.targetReturn === 'ytd') {
            sp500Return = Number(sp500Index?.price?.ytdReturn) || 0;
        }

        this.graphItems = _.compact([
            ..._.map(this.sectorPerformance, (sector) => {
                let targetReturn = 0;

                if (this.targetReturn === 'daily') {
                    targetReturn = Number(sector.price?.dailyReturn) || 0;
                } else if (this.targetReturn === 'mtd') {
                    targetReturn = Number(sector.price?.mtdReturn) || 0;
                } else if (this.targetReturn === 'ytd') {
                    targetReturn = Number(sector.price?.ytdReturn) || 0;
                }

                return {
                    name: sector.nameKo || '',
                    changePercent: targetReturn,
                    data: {
                        sectorSlug: sector.slug,
                    },
                };
            }),
            {
                name: 'S&P500',
                changePercent: sp500Return,
                isBenchmark: true,
            },
        ]);
    }

    changeTargetReturn(target: 'daily' | 'mtd' | 'ytd') {
        this.targetReturn = target;
        this.makeGraphItems();
    }

    get highlightPercent() {
        return this.targetReturn === 'daily' ? 3 : this.targetReturn === 'mtd' ? 5 : 10;
    }
}

const SectorBriefCard = observer(({ className }: { className?: string }) => {
    const viewStore = useMobxInstance(ViewStore);

    const { navigate } = useCommonNavigate();

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    return (
        <div>
            <ChangeBarGraph<{ sectorSlug: string }>
                items={viewStore.graphItems}
                highligthPercent={viewStore.highlightPercent}
                className="-mx-5 mt-4 grow justify-between gap-2"
                isShowArrow={(item) => {
                    return !item.isBenchmark;
                }}
                onClickBar={(item) => {
                    if (item.isBenchmark) {
                        return;
                    }

                    navigate(`/daily-brief/sector/${item.data?.sectorSlug}`);
                }}
            />
            {viewStore.briefDate && (
                <TargetReturnButton
                    className="mt-8"
                    targetDate={viewStore.briefDate}
                    targetReturn={viewStore.targetReturn}
                    onChangeTargetReturn={(target) => viewStore.changeTargetReturn(target)}
                />
            )}
        </div>
    );
});

export default SectorBriefCard;
